// Getting work ONTO a box: the expectation a dispatch is graded against, the plan
// it was scoped from, the dispatch decision itself and why it was refused, the
// assignment — and then the whole BOX lifecycle it lands on, enrollment through
// the self-update and release ledger.
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_DISPATCH = {
  // Annotate-expected-before-acting (#26): the model commits to a success
  // criterion *before* a consequential action, then records pass/fail
  // against it afterward. The two rows are audit evidence that the bot did
  // not rationalise a bad result as success.
  ExpectationRecorded: 'expectation.recorded',
  ExpectationChecked: 'expectation.checked',
  // #4009: a scout's plan REFUSED at admission because it cannot satisfy the brief
  // it was given — the wrong group count, or a chain where independence was
  // demanded. (A files rule — a group reaching outside, or declaring none of, the
  // files the brief named — was measured and dropped before this shipped: 71.6% of
  // groups declare no `files_touched` in a 628-plan prod replay, so a brief that
  // merely cites a path would refuse by base rate; #4034 carries the corpus-backed
  // version.) One row per refused `plan_authored`, carrying the constraint and
  // what the plan had; nothing was minted, so this row and the narrated run turn
  // are the only durable record that the plan arrived void rather than never
  // arriving at all.
  PlanRefused: 'plan.refused',
  // #4009 round 3: a group that PASSED the brief check above was still dropped
  // during MINT — a per-task throw (a rejected insert, an envelope
  // re-validation failure), never a fence, and caught AFTER siblings may
  // already be committed. NOT a refusal: this file's own best-effort isolation
  // contract means the plan proceeds with whatever landed, so there is no
  // transaction to roll back and nothing to undo. Without this row a two-group
  // brief that silently delivers one via this path is invisible to the asker —
  // the same silent-under-ship shape `PlanRefused` exists to close, reached
  // through the one door a pre-mint fence cannot close.
  PlanGroupDropped: 'plan.group_dropped',
  // `plan execute`: a committed plan directory was read and recorded as ONE plan.
  // One row per job, carrying every unit the extraction proposed and what the
  // adjudicator kept — the file sets as grounded, the paths stripped because they
  // lie in the plan directory, and each dependency edge with the plan text that
  // justifies it. `output.phase` says whether the groups were written held (a dry
  // run) or also released. The derivation is model-made, so this row is what
  // makes it inspectable rather than trusted.
  PlanExecuted: 'plan.executed',
  // `plan execute` refused: the directory was unreadable, the account has no
  // model, the extraction failed, or the adjudicator refused the manifest (a
  // cycle, an unknown edge, too many units). Nothing was written; this row is the
  // only answer the asker gets, so it names the reason and the detail.
  PlanExecuteRefused: 'plan.execute_refused',
  // #5129: `plan file` — a plan directory filed inline. One row per FILING:
  // `output` carries `{ plan_id, path, revision, filing }` (`filing` is `new` /
  // `unchanged` / `revised`). The lane's own `plan.executed` row follows when the
  // executor reads it. Rows before #6124 include refusals, so filings alone read
  // `kind = 'plan.file_requested' AND NOT (output ? 'problems')`.
  PlanFileRequested: 'plan.file_requested',
  // #6124: the same door REFUSED (`deps/plan-file.ts` `auditRefusal`), `output`
  // `{ problems: kinds }`. Its own kind so refusals are queryable on `kind`; before
  // it they shared `plan.file_requested`, told apart only by `output`. Not the #2270
  // note's query (they were never tool-call rows). History reads
  // `kind = 'plan.file_refused' OR (kind = 'plan.file_requested' AND output ? 'problems')`.
  PlanFileRefused: 'plan.file_refused',
  // #5130: a re-file of an ALREADY-FILED plan revised the same plan
  // (`filing: 'revised'`). The `plan.execute` lane's revise path landed and
  // the door's `PlanFileRequested` row stamped `filing: 'revised'` — this
  // row is its sibling, the worker-side audit, and is what terminal dashboards
  // pivot on to surface "this plan was revised at <time>" without re-reading
  // the door row's outcome. `output` carries the from/to revision and the
  // group ids that were superseded.
  PlanRevised: 'plan.revised',
  // #2270: an operator / control-plane verb that was filed as `mcp_tool.call.*` with its
  // name in `tool`. The value IS that name, so a new row's `kind` equals the old rows'
  // `tool`. For a name that was NEVER split, one query reads both:
  // `kind = $1 OR (kind LIKE 'mcp_tool.call.%' AND tool = $1)`. A name #6124 split by
  // verdict (an ok/err pair, or `plan.file_refused`) has its own reads, beside it.
  // `plan execute` QUEUED (`deps/plan-execute.ts`), and the account's plan-file
  // switch set (`deps/plan-file-switch.ts`); the lane's `plan.executed` follows.
  PlanExecuteRequested: 'plan.execute_requested',
  PlanFileSwitchSet: 'plan.file_switch_set',
  // #7012: a person set (or cleared) a plan's model — the epic's one AI model, applied
  // from the next slice claimed (`apps/api/src/deps/plan-model.ts`). One row per CHANGE:
  // setting the model a plan already has writes none. `output` names `from` and `to`.
  PlanModelSet: 'plan.model_set',
  // #6751 (epic #6552): a person paused (or resumed) a whole epic through the plan pause
  // door (`apps/api/src/deps/plan-pause.ts`). The pause stamps `plans.paused_at`, which the
  // claim gate reads to hold every waiting slice; each running slice it reached writes its
  // own `task.*` row. One row per CHANGE: a press on an already-paused plan writes none.
  PlanPaused: 'plan.paused',
  PlanResumed: 'plan.resumed',
  // An operator re-queued a CI job (`deps/task-operator.ts`, `deps/box-claims.ts`).
  CiJobRequeued: 'ci_job.requeued',
  // #6124 (the #2270 note above `PlanExecuteRequested`): promoted from `mcp_tool.call.*`.
  // `plan start` (`deps/plan-start.ts`), one row per sub-scout: the scout task minted
  // (`dispatched`), minted held for approval (`held`), or minted to return context and
  // author nothing (`context`, #3358); or the start refused for capacity (`refused`).
  // The raw-task path: the ask filed, and the ask whose nudge failed (at `alert`, the
  // only trace: `raw-task-sweep` cannot see a `pending` row).
  PlanStartDispatched: 'plan.start_dispatched',
  PlanStartHeld: 'plan.start_held',
  PlanStartContext: 'plan.start_context',
  PlanStartRefused: 'plan.start_refused',
  PlanAskFiled: 'plan.ask_filed',
  PlanAskUnnudged: 'plan.ask_unnudged',
  // A CI job's stored log ref named another job's key, so no URL was signed (`deps/ci-log.ts`).
  CiLogRefRefused: 'ci_log.ref_refused',
  // #132: the dispatch router. `dispatch.created` lands whenever a sink accepts
  // (or fails — the failure is logged on the same kind with an error output);
  // `dispatch.escalated` when the confidence gate refuses to dispatch. Sink
  // capability refusals reuse `policy.gate.refused`.
  DispatchCreated: 'dispatch.created',
  DispatchEscalated: 'dispatch.escalated',
  // #829: a triage verdict — the verdict (schedule | escalate | drop-noise |
  // dupe-of), the judged confidence, and the similarity evidence the gate
  // compared. Pre-session and pre-dispatch (the judge runs outside any
  // ToolContext), so — like `dispatch.escalated` — this row is the
  // ONLY trail of the judgment. A drop is never silent: it IS this row.
  //
  // #830 (epic #828): the dedup-as-gate in the dispatch router is the second
  // emitter (`dupe-of` on an in-motion similarity match — the cross-link lives
  // in this row, since a dropped candidate mints no task of its own). Audit is
  // trust: a task that was NOT dispatched must be as traceable as one that was.
  TriageVerdict: 'triage.verdict',
  // Plan 2026-11-03/101 D13: a MEMBER's ask minted a leg on an enabled repository the ask
  // did not name (`askMayWidenRepos`, `@developerz/agent` `triage/repo-fence.ts`). ONE row
  // per widened leg, written after the plan is recorded: `{ raw_task_id, plan_id, group_id,
  // task_id, repo_full_name, ask_repos }`, so a wrong-repo placement is countable by query.
  TriageRepoWidened: 'triage.repo_widened',
  // Plan 101 slice 08: the asker ANSWERED a `needs_info` question and the ask
  // re-entered triage — `awaiting_info -> pending` in one transaction, verdict,
  // `settled_at` and `attempts` cleared, the Q&A appended to the ask. One row per
  // answer, from either door (the blocker answer verbs, or the reporter's
  // `issue_comment` through the intake sink). The re-entry is a state change the
  // trail must show: without it a re-triaged ask reads as filed twice, or as a
  // settle that silently un-happened.
  TriageInfoAnswered: 'triage.info_answered',
  // #6124: an answer that arrived while the ask was still IN triage (plan 101 L9b,
  // `deps/blockers.ts`): recorded against the in-flight ask, re-entering nothing.
  TriageInfoAnswerHeld: 'triage.info_answer_held',
  // #7105: the queue judge's pass over one repo's pending board. ONE row per pass,
  // accepted or refused: the answer, `validateQueueJudgement`'s verdict (its refusals
  // when refused), and what the apply landed — each task merged, dropped (with its
  // evidence), kept with its rank. A drop or merge is never silent: it IS this row,
  // beside the `task.cancelled` row of each task it cancelled.
  QueueJudged: 'queue.judged',
  // #7105: a task the queue controller cancelled came back to `pending` — a reopened
  // issue (`issue_closed`), or a judge's `revive` (`queue_merged` / `queue_dropped`).
  // Its own kind because a revive undoes an audited cancel, and the trail must show the
  // undo as plainly as the cancel. Reader-first with `queue.judged`: no writer yet.
  QueueTaskRevived: 'queue.task_revived',
  // #6759 (epic #6552): the machine-first model call. `model_call.offered` is the offer of
  // one `model_call_jobs` row to the account's org machines (what was offered, its size,
  // the cluster's pick, how many machines were eligible); `model_call.settled` is who
  // answered it — the machine, or the cluster and why. Typed outputs:
  // `ModelCallOfferedOutputSchema` / `ModelCallSettledOutputSchema` (`./payload.ts`).
  // Reader-first: no writer this release (#6762 is the first caller).
  ModelCallOffered: 'model_call.offered',
  ModelCallSettled: 'model_call.settled',
  // 2026-11-01: a caller's drain HANDED BACK an open offer — left open and unsettled for
  // the resumed caller to adopt by its `caller_ref`, else for the dead-caller sweep past
  // its `deadline_at`. Not a settle (the one `model_call.settled` row still comes later);
  // the record that the caller stopped waiting and why the box kept the task.
  // Typed output: `ModelCallHandedBackOutputSchema` (`./payload.ts`).
  ModelCallHandedBack: 'model_call.handed_back',
  // Backpressure on the `queued` fan-out sink: the org's queue was already at its
  // `entitlements().maxQueueDepth`, so the enqueue was refused (no task row). A
  // refusal is a decision the trail must show — audit is trust — so it earns a
  // row, distinct from `dispatch.created` (which attests an accepted enqueue).
  DispatchQueueFull: 'dispatch.queue_full',
  // #3630: the assigner found the account AT ITS AI-DEV CAP (`entitlements().aiDevs`
  // slots all running) and held the row `seat_capped` rather than claim it. A hold
  // is a dispatch decision the trail must show — the same reason `dispatch.queue_full`
  // earns a row — and this one is the one whose remedy is money or a grant, so an
  // operator asking "why did nothing run for this org on Tuesday" needs it named.
  DispatchSeatCapped: 'dispatch.seat_capped',
  // #3630: the owner was TOLD about the cap (the notification lane fired once per
  // cap episode, not per tick). Its own kind, distinct from the hold above: one
  // says work waited, the other says a human was asked to act — and a hold with
  // no notification row is exactly the silent stall #1899 is about.
  DispatchSeatCapNotified: 'dispatch.seat_cap_notified',
  // #3630: the hold LIFTED — a slot freed (a task finished, a seat was bought, a
  // grant landed) and the assigner retracted `seat_capped`. The symmetric half of
  // `dispatch.seat_capped`: a stamp with no recorded clear leaves "how long was this
  // org capped on Tuesday" unanswerable from the trail.
  DispatchSeatCapCleared: 'dispatch.seat_cap_cleared',
  /**
   * Dispatch resolved NO BYOK credential for a claim, and WHY — one arm of
   * `NoCredentialReason` (`apps/worker` `dispatch/llm-resolver.ts`, which owns the
   * list and what each arm means): `no_pool`, `models_parked`, `lane_fenced`,
   * `all_cooled`, `keys_cooling`, and two arms from plan 102 slice 07b, where this
   * row is the refusal's only durable record: `model_pin_unserved` (no pool key serves
   * the template's pinned model, not even a cooling one), and `pool_read_failed` (the
   * pool or template-pin read threw), which any lane that resolves a credential can
   * write — coding, review, scout and setup. Which lanes still ship a claim past
   * each arm is that module's to say.
   *
   * Its own kind for the reason {@link DispatchQueueFull} has one: a refusal is a
   * decision the trail must show. Deliberately NOT `policy.gate.refused`, which is
   * the maintainer policy engine's tool gate — reusing it would pollute the indexed
   * column operators query by kind.
   *
   * The gap it closes is measured. A three-day collapse of the reviewer to
   * no-model-at-all (#2846, cause fixed in #2841) was found BY HAND, because the
   * only trace dispatch left was a `console.error` in a rotated pod log. On the
   * box the four causes are indistinguishable — `llm_credential === undefined`
   * either way — so the reviewer posts a floor review that reads as a clean bill
   * of health.
   */
  DispatchNoCredential: 'dispatch.no_credential',
  /**
   * #3040: the mothership COOLED a pool key off a runner's `key_failed` event and
   * recorded it in the task's skip set (`key-failure-ingest.ts`) — one row per
   * event, on EVERY outcome. `output.outcome` names what happened to the task:
   * `reassigned` (re-pended, the sibling `task.reenqueued` row records the move),
   * `reassign_noop` (the CAS found it already pending/terminal), or `cooled_only`
   * (the box sent `reassign: false` — it still owns the run). Its own kind because
   * the cool-only path moves no task and so writes no `task.reenqueued` row; a key
   * stepping aside is a dispatch state change and must never leave only a log line.
   * Never `billing.key.*` — those are the credential's lifecycle, and a cooled key
   * is still valid.
   */
  DispatchKeyCooled: 'dispatch.key_cooled',
  /**
   * #6378: the mothership REFUSED a runner's `key_failed` event because the key it names
   * is not one its own claim `(task, lease_epoch)` holds (`key-failure-ingest.ts`
   * `fenceReportedKey`): nothing was cooled, skipped, row-written, taught to the
   * concurrency learner or re-enqueued. `output.why` is `not_assigned`,
   * `assignment_not_found` or `no_lease_epoch`. Its own kind because a refusal is not a
   * cool, and every `dispatch.key_cooled` count must stay a count of cools. NOT visible
   * to the maintainer: the named key id may be another account's.
   */
  DispatchKeyFailedRefused: 'dispatch.key_failed_refused',
  // A released claim prompted an audited clear of current key_saturated facts.
  // Advisory only: normal dispatch still rechecks eligibility and acquires capacity.
  DispatchKeyCapacityWoken: 'dispatch.key_capacity_woken',
  /**
   * The MODEL ESCALATION LADDER's decision for one coding claim it was armed on
   * (`value-objects/llm-credential/escalation.ts`, 2026-09-24): a claim whose saved WIP
   * stopped getting closer, or a stuck-PR RESCUE round (#6172). `output.trigger` names which
   * (`ModelEscalationTrigger`: `gate_stall {from, to, streak}`, `stuck_pr {reason,
   * parked_ms}`, or `sent` for work sent to Escalation on one machine, #6767; rows before
   * #6172 carry a bare `stall` instead). `output.decision` is
   * `escalated` (the claim sealed the stronger model: `from`/`to` name both, `via` the rung
   * — the account's `escalation` route or its tier tags) or `skipped` (`output.reason` says
   * why: nothing stronger named, the route unserved, a spend cap, or the claiming machine's
   * own Escalation switch off (`machine_role_off`) or unreadable (`machine_switch_unread`),
   * #6758 — `input.runnerId` names that machine). Written on EVERY armed
   * claim, because "why did my stuck task not get a stronger model" is otherwise
   * unanswerable. Rare by construction — the kind's own index is how `task outcome` reads
   * it back per task.
   */
  DispatchModelEscalated: 'dispatch.model_escalated',
  // #121: the pull-assigner claimed a ready task for a runner (`pending →
  // assigned`) and wrote the `fleet.task.<runnerId>` push to the outbox. A
  // fleet-routing state change (which box got the work), so it gets its own
  // audit row — audit is trust, even for internal dispatch mechanics.
  TaskAssigned: 'task.assigned',
  // Plan 102 slice 01: a pending task was HELD — a `tasks.hold_reason` stamp. Its
  // writers: the dispatcher pass's `mark*Holds` family in `@developerz/db`
  // (`queries/tasks/claim.ts` — credential cooling, area, capacity, onboarding,
  // mode, upstream group, box fault, capable box, review-awaiting-model), and the
  // failure re-pend's windowed hold (`failOrRetryTask`, `taskFailureRependHold`).
  // The column is overwritten in place, so without this row a hold's duration is
  // not measurable. `output` is
  // `TaskHeldOutputSchema` (`./payload`): `{ task_id, reason, until }`.
  TaskHeld: 'task.held',
  // #110: BYOVM runner lifecycle. `runner.enrolled` lands on the first dial-out
  // (a new machine identity is created — security-relevant, like AccountPaused).
  // `runner.stale_offline` lands when the presence reaper (#117) flips an online
  // runner offline because its `FLEET_PRESENCE` KV key lapsed. `runner.revoked`
  // lands when an operator kills a runner's credential.
  RunnerEnrolled: 'runner.enrolled',
  RunnerStaleOffline: 'runner.stale_offline',
  // #4400 (epic #4391, Terminal slice 09): the symmetric half `stale_offline`
  // never got. `runner.stale_offline` lands when the presence reaper flips an
  // online runner offline; nothing marked a box COMING BACK, so a recovery was
  // invisible while the failure was loud — and the write itself was already
  // flagged unaudited (`apps/worker/src/cron/runner-sweep.ts` #1510). Fires on
  // the revive pass's own `offline -> online` flip ONLY — the same guard
  // `notifyBoxOnline` already takes — never per presence beat, so a flapping box
  // earns one row per genuine transition, not one per tick.
  RunnerOnline: 'runner.online',
  RunnerRevoked: 'runner.revoked',
  // A `pending` enrollment whose one-time token expired unused, DELETED by the
  // sweep (`reapExpiredPendingEnrollments`, PR "runner fleet safety"). The row
  // never became a box, so the delete is the only trace it existed: one
  // account-scoped row per reaped enrollment, `input: { runnerId }`.
  RunnerEnrollmentExpired: 'runner.enrollment_expired',
  // Plan 101 runner lifecycle, slice 02: an operator took a box OUT of the fleet in
  // one act — drain (its in-flight work re-queued), revoke (row, gateway tokens, the
  // `FleetBoxRevoked` halt on its control channel, presence key deleted). Its own
  // kind rather than a `runner.revoked` with a note, because the question the trail
  // answers is different: "who killed this credential" vs "who retired this machine".
  // The `runner.drained` + `runner.revoked` rows the composed steps write still land.
  RunnerDecommissioned: 'runner.decommissioned',
  // #622: the box ran `scripts/runner/harden.sh` and reported what it found —
  // the firewall + docker↔UFW verdicts and every risky service still listening
  // on a public address. A REPORT, never a gate: it decides nothing about the
  // box's credential or its work. It exists because "the box came up with a
  // passwordless Postgres open to the internet" is precisely the fact that must
  // leave a trail an operator can find, centrally, rather than only in the
  // scrollback of whoever ran the install.
  RunnerHardeningReported: 'runner.hardening_reported',
  // #2186: the box rolled its own NATS credential before it expired. The row's
  // `nats_creds_ref` names a different key afterwards, so this is an identity
  // state change and not telemetry — and under a MEMORY resolver (no per-user
  // revocation list) it is the ONLY record that can answer "did this box roll
  // forward after we revoked it", which is the question a bounded credential
  // lifetime leaves open for exactly one lifetime.
  RunnerCredsReissued: 'runner.creds_reissued',
  // #2270 (the note above `PlanExecuteRequested`): promoted from `mcp_tool.call.*`.
  // The rest of `RunnerAuditEvent` (`apps/api/src/routes/runners.ts`): an enrollment
  // minted or refused (402), the SSH-pull queue, and the audited enroll-time downgrades.
  RunnerEnrollmentCreated: 'runner.enrollment_created',
  RunnerEnrollRefused: 'runner.enroll_refused',
  RunnerSshBootstrapQueued: 'runner.ssh_bootstrap_queued',
  RunnerSshBootstrapQueueFailed: 'runner.ssh_bootstrap_queue_failed',
  RunnerLogSinkDeliveryFailed: 'runner.log_sink_delivery_failed',
  RunnerApiKeyMintFailed: 'runner.api_key_mint_failed',
  RunnerOrgLookupFailed: 'runner.org_lookup_failed',
  // The enrolling box's template could not be read (`deps/runner-enroll.ts`).
  RunnerTemplateLookupFailed: 'runner.template_lookup_failed',
  // The revoke's best-effort queue retire, and its miss (`deps/army.ts`).
  RunnerQueueRetired: 'runner.queue_retired',
  RunnerQueueRetireFailed: 'runner.queue_retire_failed',
  // #6422: the row flip landed and the work AFTER it did not — the gateway-token
  // cascade, the claim release or the halt of a box retired by a re-join
  // (`apps/api/src/routes/runner-rejoin.ts`). Its own kind rather than an `error`
  // field on `runner.revoked`, because the revocation and its cleanup are two facts
  // with two lifetimes: the flip is committed and permanent, the cleanup is
  // idempotent and retryable, and a trail that folds them into one row can say
  // neither "this box IS revoked" nor "its credential may still be live" without
  // the other. The retry is an operator's `box revoke` of the same id.
  RunnerRevokeCascadeFailed: 'runner.revoke_cascade_failed',
  // The operator's box verbs (`deps/box-*.ts`, bound in `deps/box-control.ts`): drain,
  // owner (and its refusal), prompt, the cross-tenant isolation attestation and its
  // withdrawal, and the release un-yank (#2270's own example: 29 prod un-yanks filed
  // under the tool kind).
  RunnerDrained: 'runner.drained',
  // The drain's undo: an operator cleared a box's drain mark (`runners.drained_at`),
  // so the presence sweep may revive it again. The row is the only record of who let
  // a held-out box back into the fleet.
  RunnerUndrained: 'runner.undrained',
  // The GRACEFUL drain: an operator cordoned a box (`runners.cordoned_at`) so the
  // assigner hands it no new work while it finishes what it holds — nothing is
  // re-queued, unlike `runner.drained`. Cleared by the same undrain (`runner.undrained`).
  RunnerCordoned: 'runner.cordoned',
  // #6751: a person paused (or resumed) every job a machine holds for this account
  // (`apps/api/src/deps/runner-pause-jobs.ts`). Unlike the drain, the box keeps taking new
  // work. Each job the call reached writes its own `task.*` row; this one names the batch.
  RunnerJobsPaused: 'runner.jobs_paused',
  RunnerJobsResumed: 'runner.jobs_resumed',
  // #6768: a person moved a job or a batch of jobs from one machine to another (the move
  // control, `apps/api/src/deps/box-move.ts`). ONE row per ACCEPTED request, in its
  // transaction, with the person as `user_id`: `{ batch_id, from, to, cost, items, undo_of }`.
  // A refused move writes nothing. Each item's landing or failure writes its own `task.moved`.
  MoveBatch: 'move.batch',
  // #7790: a box's grant to another account. Offered and revoked rows land on the box's account,
  // accepted on the grantee's, each with the acting person as `user_id`, in the write's
  // transaction: `{ grant_id, box_id, grantee_account_id, repos, max_slots }`. A refusal writes
  // `{ box_id | grant_id, reason }` durably, because it is that refusal's only trace.
  BoxGrantOffered: 'box_grant.offered',
  BoxGrantAccepted: 'box_grant.accepted',
  BoxGrantRevoked: 'box_grant.revoked',
  BoxGrantWriteRefused: 'box_grant.write_refused',
  // A reservation a former guest left on the box, released so the home account is not held
  // hostage: the grant stopped reaching with no door involved (an owner left, a re-offer).
  // `{ runner_id, task_ids, reason }`, on the guest's chain, with no person: the assigner did it.
  BoxGrantReservationsReleased: 'box_grant.reservations_released',
  RunnerOwnerSet: 'runner.owner_set',
  RunnerOwnerSetRefused: 'runner.owner_set_refused',
  RunnerPromptSet: 'runner.prompt_set',
  // #2268: `PATCH /v1/runners/:id` set or cleared a box's `display_name`
  // (`deps/box-rename.ts`). The row is the only record of the name it REPLACED.
  RunnerRenamed: 'runner.renamed',
  // #6556: `PUT /v1/runners/server-label` set or cleared a box's `server_label`
  // (`deps/box-server-label.ts`) — ONE row per machine a call labelled. The row is
  // the only record of the label it REPLACED.
  RunnerServerLabelled: 'runner.server_labelled',
  // #6767: `POST /v1/runners/:id/work` (MCP `box send`) minted a task with a HARD target
  // on one machine (`tasks.target_runner_id`, until `target_until`). Written in the mint's
  // transaction, `userId` the sender; `{task_id, runner_id, role, repo, number,
  // note_chars}`. The row is the only record of who put the work on that machine.
  TaskSentToMachine: 'task.sent_to_machine',
  // #6767: `cron.target_release` cleared a sent task's target past its `target_until`
  // (still `pending`, or claimed by another machine through the claim's expired-target
  // arm). ONE row per cleared task, in the clear's transaction; `{task_id, runner_id,
  // target_until}`, `runner_id` the machine the target named. The row is the only
  // record of why sent work ran somewhere else.
  TaskTargetReleased: 'task.target_released',
  // #6740: `PUT /v1/runners/:id/max-slots` set ONE box's `max_slots`
  // jobs-at-once cap (one `_set` row, previous and new in the payload); `POST
  // /v1/runners/max-slots/reset`, MCP `box reset_max_slots` and
  // `scripts/runner-max-slots-reset.ts` set EVERY live box of an account to 1
  // (one `_reset` row per account, `{ source, count, runners: [{ id,
  // previousMaxSlots }] }`; `deps/box-max-slots.ts`). The row is the only
  // record of the cap a box held BEFORE.
  RunnerMaxSlotsSet: 'runner.max_slots_set',
  RunnerMaxSlotsReset: 'runner.max_slots_reset',
  RunnerIsolationAttested: 'runner.isolation_attested',
  RunnerIsolationWithdrawn: 'runner.isolation_withdrawn',
  RunnerReleaseUnyanked: 'runner.release_unyanked',
  // #6735: a platform owner ASKED for the promote of a soaked canary runner build to
  // stable (`POST /v1/runners/releases/:version/promote`, MCP `platform promote`). The
  // row names the person and the canary build; it is committed BEFORE the CI run that
  // does the promote is dispatched, so `cron.fleet_autoroll`'s `runner.release_recorded`
  // row for that version carries its request id (`promotion.requestId`).
  RunnerPromoteRequested: 'runner.promote_requested',
  // #6735: the dispatch of that run definitely failed AFTER its request row committed
  // (GitHub was never reached, or answered no). Frees the version for an immediate
  // retry (the in-flight refusal skips a failed request).
  RunnerPromoteDispatchFailed: 'runner.promote_dispatch_failed',
  // #6735: the dispatch was SENT and its answer was lost (a reset, a timeout, a 5xx), so
  // GitHub may have started the run. Frees nothing: the request keeps holding the
  // version, and stays its release's attribution, for `RUNNER_PROMOTE_IN_FLIGHT_MS`.
  RunnerPromoteDispatchUnknown: 'runner.promote_dispatch_unknown',
  // Plan 101 runner lifecycle, doc 05 #5: the BOX's own report of what its credential
  // roll concluded, landed by the fleet box-event sink from
  // `fleet.box.<runnerId>.creds_refresh`. `creds_reissued` above is written by the
  // API when it MINTS; it therefore cannot record the roll that never reached the
  // API — a control plane that was down, a token the mothership refused, a disk
  // the box could not write, or a re-issued credential whose `exp` did not advance
  // (#2732). Every one of those was a journal line on the box and nothing the
  // mothership could see, so `box_creds_lapsed` fired with no history behind it.
  RunnerCredsRefreshReported: 'runner.creds_refresh_reported',
  // Plan 101 F4: the BOX reported an assignment it could not decode, landed by the
  // box-event sink from `fleet.box.<runnerId>.assignment_undecodable` BEFORE the box
  // dropped the push. Until it existed the drop was a journal line and the task sat
  // `assigned` for the 180-min stuck-task window, then went back to the same box.
  RunnerAssignmentUndecodableReported: 'runner.assignment_undecodable_reported',
  // #368: the box's PINNED role changed on the row. Every runner-role mutation
  // must produce a per-runner transition event — audit is trust, especially for
  // dispatch-gate telemetry: the assigner's `listAssignableRunners` reads
  // `runners.mode`, so a role write silently reshapes what work the box can
  // claim. Fired by BOTH write paths (the API heartbeat and the presence
  // reaper's mode-sync pass) via the shared `runner.mode_changed` audit
  // contract, so no mode mutation can complete without a matching row. Carries
  // the runner identity, previous role, new role, and mutation source.
  RunnerModeChanged: 'runner.mode_changed',
  // arc 101 doc 03: the mothership folded a setup runner's `capabilities_detected`
  // advert into `runners.capabilities.provisions`. Same posture as
  // `runner.mode_changed` — a dispatch-gate column changed, so it earns a row: the
  // assigner matches a task's `requires` against `provisions`, meaning this write
  // reshapes what work the box can be handed. It is also the only durable trace
  // that an UNTRUSTED box widened its own advert (the merge is additive + capped,
  // so the row records exactly which tokens were added and which were refused).
  RunnerCapabilitiesUpdated: 'runner.capabilities_updated',
  // arc 101 doc 08 step 3: every transition the staged self-update rollout makes. The
  // rollout hands an UNTRUSTED box a new binary to run as root, so every step of
  // that decision is the trail — audit is trust, and here it is also the only
  // account of who was told to install what.
  //   `published`   — a `SelfUpdateRequest` for one box went into the outbox
  //                   (durable intent; the relay puts it on the wire).
  //   `confirmed`   — that box came back reporting the pinned build within the
  //                   confirm deadline (its presence beat is the ONLY proof; a
  //                   publish is intent, never installation).
  //   `failed`      — the deadline passed with the box still off the build, or a
  //                   confirmed box regressed during the soak window.
  //   `halted`      — the failure rate crossed the threshold: `runner_releases.
  //                   yanked_at` was stamped and no further wave may install it.
  //   `rolled_back` — a box that DID take the halted build was published the
  //                   previous release. Distinct from `published` because it
  //                   moves the fleet backwards, which must never read as a
  //                   normal wave in the trail.
  //   `retargeted`  — a release published mid-pass superseded the target the pass
  //                   opened on (#1680), and its per-target verdict counters were
  //                   reset. The only hop that is not about a box: the `published`
  //                   rows after it name the new version but cannot say a SWITCH
  //                   happened (their `fromVersion` is the box's old build, and the
  //                   audit chain is per ACCOUNT, not per pass), so without this row
  //                   the move and the evidence it discarded live only in a log line.
  // plan 101 02-2: an `outbox` row the relay retired after its last allowed publish
  // attempt (`dead_at` set; an oversized payload, a schema refusal). The row is an
  // UNDELIVERED fleet message — a task assignment, an update push — so its retirement
  // is on the platform chain with the subject, the attempts and the last error, and
  // an assignment's task is re-pended beside it. Platform-only: never a console line.
  OutboxDeadLettered: 'outbox.dead_lettered',
  RunnerUpdatePublished: 'runner.update_published',
  RunnerUpdateConfirmed: 'runner.update_confirmed',
  RunnerUpdateFailed: 'runner.update_failed',
  //   `update_refused` — a box DECLINED a build on purpose (a boot-time config guard
  //                      fired) and the controller attributed that refusal to the BOX,
  //                      not the release (#1917). Deliberately its OWN kind and NOT a
  //                      flavour of `update_failed`: `readReleaseInstallEvidence`
  //                      selects `update_confirmed`/`update_failed` and buckets
  //                      everything not-confirmed into `failed` REASON-BLIND, so
  //                      filing a correct refusal there would convict the build it
  //                      exonerates. A refusal the controller could NOT attribute to
  //                      the box still writes `update_failed`, because at that point
  //                      the build genuinely is the leading suspect.
  RunnerUpdateRefused: 'runner.update_refused',
  //   `boot_refusal_reported` — the BOX's own raw report that a boot-time config guard
  //                             fired, landed by the fleet event sink. Distinct from
  //                             `update_refused` above, which is the CONTROLLER's
  //                             derived attribution decision over a history of these.
  //                             One is testimony, the other is the verdict; collapsing
  //                             them would make a refusal its own proof that the box
  //                             is chronically misconfigured.
  RunnerBootRefusalReported: 'runner.boot_refusal_reported',
  //   `excusal_streak` — one box has now been excused from MORE distinct versions than
  //                      the bound allows (#2223). Its own kind rather than a field on
  //                      `update_refused`, because the two answer different questions
  //                      and are written at different moments: that row records ONE
  //                      excusal and is correct on its own, while this one is the claim
  //                      that the SERIES has become a standing fault. A log line was
  //                      the first implementation and is not durable — an operator
  //                      asking "when did this box stop taking releases" needs a row
  //                      the prune crons exempt, not a retention window.
  RunnerExcusalStreak: 'runner.excusal_streak',
  //   `log_reported` — a box archived a bounded slice of its OWN process log and
  //                    told us where it went. ONE ROW PER TRIGGER, never per line:
  //                    this table is a tamper-evident hash chain that every prune
  //                    cron exempts by name (`prune-run-event-payloads.ts`), so a
  //                    per-line write would grow a table nothing may ever delete.
  //                    The row therefore holds a POINTER (`logRef`) plus a `<2KB`
  //                    tail, exactly the `ci_jobs.log_ref` + `log_excerpt` shape,
  //                    and the bytes live in R2 under the `logs/` prefix whose
  //                    3-day lifecycle rule already existed.
  //                    DISPLAY-ONLY. Unlike `boot_refusal_reported` beside it —
  //                    testimony the rollout controller grades — nothing may key a
  //                    decision on this row. Its contents are unverified box
  //                    narration, and a box able to move a verdict by choosing what
  //                    to print would be deciding its own case.
  RunnerLogReported: 'runner.log_reported',
  RunnerUpdateHalted: 'runner.update_halted',
  RunnerUpdateRolledBack: 'runner.update_rolled_back',
  RunnerUpdateRetargeted: 'runner.update_retargeted',
  // plan 101 06-15: a pass that did NOT take a newer stable release because the
  // platform account's pass for it is not `complete` (the cross-account canary,
  // `apps/worker/src/fleet/platform-canary.ts`). Its own kind, never a
  // `retargeted` row: nothing moved, and the trail must not say it did.
  RunnerUpdateRetargetHeld: 'runner.update_retarget_held',
  // 2026-11-04: a pass that ENDED while this box was still waiting a live run out
  // behind a push (an unsettled `update_deferred` with no ceiling) or draining before
  // any push could land. The pass stops watching it, so one never-idle box cannot hold
  // its channel's rollout open; the box's own `update_verified` /
  // `update_boot_rolled_back` rows still record the install it does later
  // (`apps/worker/src/fleet/rollout-handoff.ts`).
  RunnerUpdateHandedOff: 'runner.update_handed_off',
  // plan 101 runner lifecycle, slice 06: the BOX's own testimony about the half of
  // self-update the controller never sees, landed by the fleet box-event sink from
  // `fleet.box.<runnerId>.update`. Five kinds, one per hop, because each is a
  // different fact with a different reader — and because the evidence reader
  // (`readReleaseInstallEvidence`) selects by kind and buckets reason-blind, so a
  // pull that merely STARTED must not be filed under the kind that vouches for a
  // build. Measured before this existed: 2,250 `update_published` rows, 2,111
  // `update_confirmed`, and ZERO rows for any install a box pulled itself, while
  // the boxes' journals showed 4-10 pulls per box per 48h.
  //   `update_pulled`           — the pre-work check ARMED an install from the channel
  //                               alias. Intent, like `update_published`; never evidence.
  //   `update_swapped`          — the verified bytes landed over the binary (pull or
  //                               push); the box is exiting into them.
  //   `update_swap_aborted`     — the swap was refused before any byte landed (the
  //                               rollback could not be prepared). A fact about the
  //                               BOX's disk, never about the build — counted by nobody.
  //   `update_verified`         — the swapped build reached health (NATS + first
  //                               presence beat). The pull-side counterpart of
  //                               `update_confirmed`, and it COUNTS as confirmation.
  //   `update_boot_rolled_back` — repeated failed boots on the build restored the
  //                               preserved binary. The box's counterpart of the
  //                               controller's `update_failed`, and it COUNTS against
  //                               the build. Its OWN kind, distinct from
  //                               `update_rolled_back` above, for the same reason
  //                               `boot_refusal_reported` is distinct from
  //                               `update_refused`: one is the box's testimony, the
  //                               other the controller's decision.
  //   `update_deferred`         — the box holds a live run and is waiting it out
  //                               before swapping; `input.waitCeilingMs` is the bound
  //                               on that wait (null ⇒ unbounded). The rollout grader
  //                               reads it to EXTEND the box's confirm deadline —
  //                               intent, never evidence: `readReleaseInstallEvidence`
  //                               does not select it. Before this row existed the
  //                               mothership graded a deferring box `confirm_deadline`
  //                               at 10 min, terminally, on a canary of one.
  //   `update_deferred_settled` — that wait ended and the swap proceeds;
  //                               `input.reason = 'ceiling_elapsed'` when the ceiling
  //                               ended it and the run was released. The grader
  //                               restarts the confirm deadline from this row.
  //   `update_fetch_failed`     — the box could not FETCH or VERIFY the build, so it
  //                               never swapped (#7083 defect D). `input.reason` is
  //                               one of `UpdateFetchFailedReason`
  //                               (`value-objects/fleet-box-event.ts`): a missing
  //                               artifact, a failed download or a bad signature. The
  //                               rollout grader reads it BEFORE silence; a fact about
  //                               the bytes or the ledger, never evidence about how
  //                               the build runs — `readReleaseInstallEvidence` does
  //                               not select it.
  RunnerUpdatePulled: 'runner.update_pulled',
  RunnerUpdateSwapped: 'runner.update_swapped',
  RunnerUpdateSwapAborted: 'runner.update_swap_aborted',
  RunnerUpdateVerified: 'runner.update_verified',
  RunnerUpdateBootRolledBack: 'runner.update_boot_rolled_back',
  RunnerUpdateDeferred: 'runner.update_deferred',
  RunnerUpdateDeferredSettled: 'runner.update_deferred_settled',
  RunnerUpdateFetchFailed: 'runner.update_fetch_failed',
  //   `rollout_resumed` — a rollout pass picked up the durable `runner_rollouts` row a
  //                       previous worker process left `in_progress` (a redeploy
  //                       mid-pass) and continued with that pass's open attempts,
  //                       rather than opening a fresh tally against the same fleet.
  //                       Without this row a resumed pass is indistinguishable in the
  //                       trail from a second pass that re-graded the first one's
  //                       publishes.
  RunnerRolloutResumed: 'runner.rollout_resumed',
  // The two writes `cron.fleet_autoroll` makes without a human in the loop.
  //   `release_recorded` — a build CI published entered `runner_releases`, which
  //                        is what makes it installable fleet-wide. The operator
  //                        verb it replaces (`bun run runner:release:record`) left
  //                        its trace in someone's terminal; a cron leaves none, and
  //                        this is the row that says which bytes the platform
  //                        decided to stand behind.
  //   `autoroll_started` — the lane asked one account's fleet to take it. NOT a
  //                        duplicate of `update_published` (per BOX, written by the
  //                        rollout controller, and it carries the actor of whoever
  //                        ran MCP `box update`): those rows are the consequence,
  //                        this one is the automatic DECISION that caused them, and
  //                        without it a fleet-wide roll appears in the trail with
  //                        no actor and no cause.
  RunnerReleaseRecorded: 'runner.release_recorded',
  RunnerAutorollStarted: 'runner.autoroll_started',
  // #1982: an R2 retention pass over `runner/releases/` (`cron.prune_runner_releases`,
  // four times a day, `PRUNE_RUNNER_RELEASES_CRON`). The counterpart to `release_recorded`: that row
  // says which bytes the platform decided to stand behind, this one says which it
  // DESTROYED. The deletion is irreversible and leaves no other durable trace —
  // absence is not evidence, and a structured log line is neither queryable,
  // chained, nor retained — so the row names every version whose prefix was
  // deleted, plus the count of survivors per keep-condition, which is what makes
  // it self-explaining: "why is version X's binary gone" and "why is 19 GB not
  // going down" are answered by the same row.
  //
  // `output.outcome` discriminates (`PruneRunnerReleasesAudit`,
  // `apps/worker/src/cron/prune-runner-releases.ts`). ONE CLOSING row per pass, ALWAYS
  // — `pruned` (the plan applied; `deleted` names every destroyed version),
  // `delete_failed` (bytes WERE destroyed before the pass stopped: `deleted`, the
  // `partial` prefix, the failing key), `mark_failed` (a `runner_releases.pruned_at`
  // write failed; nothing deleted) or `refused` (a read failed; nothing touched) —
  // including the pass that deleted nothing, because a tick with no closing row is a
  // schedule that stopped, which is the failure this lane exists to make impossible.
  // Since #7083 a pass that marks anything ALSO writes a `marked` row first, strictly,
  // before the first `pruned_at` write (`retention` and `bytesMissing` name the versions
  // it is about to mark), so "one closing row per tick" filters `outcome <> 'marked'`.
  // Written on the lane's PLATFORM chain (`audit: { kind: 'platform' }`, one parent per
  // lane via `platformSessionParentId`) since #3236, which ended the job-audit-store
  // binding that had left prod with zero rows of this kind.
  RunnerReleasePruned: 'runner.release_pruned',
  //   `boot_release` — the boot-release RESPONDER's verdict, in ONE row: the
  //                    box booted holding work, so the mothership reset its
  //                    in-flight tasks, re-queued its live CI jobs and purged
  //                    its pending pushes. Its OWN kind and not
  //                    `runner.stale_offline` (the REAPER's verdict — presence
  //                    lapsed, the OPPOSITE transition, a different reader),
  //                    and not `task.released` (the drain's per-task row):
  //                    conflating either would pollute the indexed kind column
  //                    operators query by transition.
  RunnerBootRelease: 'runner.boot_release',
} as const;
